import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Download, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map backend color names to Tailwind cell classes.
const fillClass = (color) => {
  if (color === 'green') return 'bg-green-100 text-green-800';
  if (color === 'yellow') return 'bg-yellow-100 text-yellow-800';
  if (color === 'red') return 'bg-red-100 text-red-800';
  return '';
};
const rowFillClass = (color) => {
  if (color === 'yellow') return 'bg-yellow-100';
  if (color === 'red') return 'bg-red-100';
  return '';
};

const REPORTS = {
  grades: {
    title: 'Concentrado de Calificaciones',
    endpoint: 'grades-report',
    excel: 'export-grades-excel',
    filename: 'Concentrado_Calificaciones.xlsx',
  },
  attendance: {
    title: 'Reporte de Asistencias y Tareas',
    endpoint: 'attendance-tasks-report',
    excel: 'export-excel',
    filename: 'Reporte_Grupo_Tutorado.xlsx',
  },
};

const GrupoTutoradoReportPage = ({ user, onLogout }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const type = params.get('type') === 'attendance' ? 'attendance' : 'grades';
  const cfg = REPORTS[type];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjectFilter, setSubjectFilter] = useState('all');
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setSubjectFilter('all');
    axios.get(`${API}/teacher/grupo-tutorado/${cfg.endpoint}`, { headers })
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) toast.error('Error al cargar el reporte'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line
  }, [type]);

  const downloadExcel = async () => {
    try {
      const res = await axios.get(`${API}/teacher/grupo-tutorado/${cfg.excel}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', cfg.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch (e) { toast.error('Error al descargar el reporte'); }
  };

  // Unique subjects for the filter, and the classes currently visible.
  const subjects = data ? [...new Set(data.classes.map((c) => c.subject))] : [];
  const visibleClasses = data
    ? (subjectFilter === 'all' ? data.classes : data.classes.filter((c) => c.subject === subjectFilter))
    : [];
  const filteredData = data ? { ...data, classes: visibleClasses } : null;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap no-print">
          <div>
            <Button variant="outline" onClick={() => navigate('/teacher/grupo-tutorado')} className="mb-2" data-testid="report-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver al panel
            </Button>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="report-title">{cfg.title}</h1>
            {data?.group_name && <p className="text-sm text-slate-600">Grupo: {data.group_name}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {subjects.length > 0 && (
              <select
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-400"
                data-testid="report-subject-filter"
              >
                <option value="all">Todas las materias</option>
                {subjects.map((s, i) => (<option key={i} value={s}>{s}</option>))}
              </select>
            )}
            <Button onClick={() => window.print()} variant="outline" className="border-slate-400 text-slate-700 hover:bg-slate-50" data-testid="report-print-btn">
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
            <Button onClick={downloadExcel} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="report-download-btn">
              <Download className="w-4 h-4 mr-2" /> Descargar Excel
            </Button>
          </div>
        </div>

        <div id="report-print-area">
          {/* Print-only heading */}
          <div className="hidden print:block mb-3">
            <h1 className="text-xl font-bold text-slate-900">{cfg.title}</h1>
            {data?.group_name && <p className="text-sm text-slate-600">Grupo: {data.group_name}{subjectFilter !== 'all' ? ` — ${subjectFilter}` : ''}</p>}
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><RefreshCw className="w-8 h-8 animate-spin text-teal-500" /></div>
          ) : !filteredData || !filteredData.classes?.length ? (
            <Card className="p-8 text-center text-slate-500" data-testid="report-empty">No hay clases para mostrar.</Card>
          ) : type === 'grades' ? (
            <GradesReport data={filteredData} />
          ) : (
            <AttendanceTasksReport data={filteredData} />
          )}
        </div>
      </div>
    </Layout>
  );
};

const GradesReport = ({ data }) => (
  <div className="space-y-6" data-testid="grades-report">
    <p className="text-xs text-slate-500">
      Escala 5–10. Aprueba la materia quien sume 18 o más entre los 3 parciales. Sombreado: amarillo = advertencia, rojo = no alcanza 18.
    </p>
    {data.classes.map((cls, ci) => (
      <Card key={ci} className="overflow-hidden">
        <div className="bg-blue-600 text-white px-4 py-2 font-semibold text-sm">
          {cls.subject} {cls.teacher_name ? `| Prof: ${cls.teacher_name}` : ''}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-blue-50 text-blue-800">
                <th className="py-2 px-2 text-left w-10">#</th>
                <th className="py-2 px-2 text-left min-w-[180px]">Alumno</th>
                <th className="py-2 px-2 text-center">Parcial 1</th>
                <th className="py-2 px-2 text-center">Parcial 2</th>
                <th className="py-2 px-2 text-center">Parcial 3</th>
                <th className="py-2 px-2 text-center">% Asistencia</th>
              </tr>
            </thead>
            <tbody>
              {cls.students.length === 0 ? (
                <tr><td colSpan={6} className="py-3 px-2 text-slate-400 italic">Sin alumnos / evaluación en esta clase</td></tr>
              ) : cls.students.map((st, si) => (
                <tr key={si} className={`border-b ${rowFillClass(st.row_color)}`}>
                  <td className="py-1.5 px-2">{si + 1}</td>
                  <td className="py-1.5 px-2 font-medium">{st.name}</td>
                  <td className="py-1.5 px-2 text-center font-bold">{st.p1 != null ? st.p1 : '--'}</td>
                  <td className="py-1.5 px-2 text-center font-bold">{st.p2 != null ? st.p2 : '--'}</td>
                  <td className="py-1.5 px-2 text-center font-bold">{st.p3 != null ? st.p3 : '--'}</td>
                  <td className="py-1.5 px-2 text-center">{st.attendance != null ? `${st.attendance}%` : '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    ))}
  </div>
);

const GridBlock = ({ title, headerColor, grid, totalHeaders }) => {
  if (!grid) return <p className="px-4 py-3 text-slate-400 italic text-sm">Sin registros</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className={`${headerColor} text-white`}>
            <th className="py-1.5 px-2 text-left w-8">#</th>
            <th className="py-1.5 px-2 text-left min-w-[160px]">Alumno</th>
            {grid.columns.map((c, i) => (
              <th key={i} className={`py-1.5 px-1 text-center whitespace-nowrap ${c.type === 'sep' ? 'bg-purple-200 text-purple-800' : ''}`}>{c.label}</th>
            ))}
            {totalHeaders.map((h, i) => (
              <th key={`t${i}`} className="py-1.5 px-2 text-center bg-slate-100 text-slate-700 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((r, ri) => (
            <tr key={ri} className="border-b">
              <td className="py-1 px-2">{r.num}</td>
              <td className="py-1 px-2 font-medium whitespace-nowrap">{r.name}</td>
              {r.cells.map((cell, i) => (
                <td key={i} className={`py-1 px-1 text-center ${cell.type === 'sep' ? 'bg-purple-100' : fillClass(cell.color)}`}>
                  {cell.type === 'sep' ? '' : (cell.value || '')}
                </td>
              ))}
              {/* totals */}
              {'att_pct' in r ? (
                <>
                  <td className={`py-1 px-2 text-center font-bold ${fillClass(r.att_color)}`}>{r.att_pct}%</td>
                  <td className={`py-1 px-2 text-center font-bold ${fillClass(r.flt_color)}`}>{r.flt_pct}%</td>
                </>
              ) : (
                <td className={`py-1 px-2 text-center font-bold ${fillClass(r.avg_color)}`}>{r.avg != null ? `${r.avg}%` : '--'}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AttendanceTasksReport = ({ data }) => (
  <div className="space-y-8" data-testid="attendance-tasks-report">
    {data.classes.map((cls, ci) => (
      <Card key={ci} className="overflow-hidden">
        <div className="bg-slate-700 text-white px-4 py-2 text-sm">
          <span className="font-semibold">{cls.subject}</span>
          <span className="text-slate-300"> | Prof: {cls.teacher_name} | Alumnos: {cls.num_students}</span>
        </div>
        <div className="px-3 py-2">
          <h3 className="text-sm font-semibold text-blue-700 mb-1">Registro de Asistencias</h3>
          <GridBlock title="Asistencias" headerColor="bg-blue-600" grid={cls.attendance} totalHeaders={['% Asist.', '% Faltas']} />
        </div>
        <div className="px-3 py-2 border-t">
          <h3 className="text-sm font-semibold text-green-700 mb-1">Registro de Tareas</h3>
          <GridBlock title="Tareas" headerColor="bg-green-700" grid={cls.tasks} totalHeaders={['% Promedio']} />
        </div>
      </Card>
    ))}
  </div>
);

export default GrupoTutoradoReportPage;
